import { useAuth } from "../context/AuthContext";
import { canClaimAsDialer, canSeeDialAnalytics } from "../config/tempDialers";
import { userTargetCurrency } from "../context/CurrencyContext";

// =============================================================================
// useRBAC - ROLE-BASED ACCESS CONTROL
// =============================================================================
// PURPOSE: Derives permission flags from user role.
//
// REQUIREMENTS:
// - Map role names to capabilities
// - hasTunnelVision: closers/setters/dialers see only own data
// - Support view-as impersonation (only exec/owner/admin can impersonate)
//
// KEY DECISIONS:
// - Uses effective role (user.role), not realUser.role for permissions
// - isRealExec checks realUser for view-as restoration
// - canDelete restricted to owner/executive only
// - isConfidential for exec/owner/admin/client portal access
// =============================================================================

export const useRBAC = () => {
  const { user, realUser } = useAuth();

  const role = user?.role;
  const isRealExec = realUser?.role === "exec";

  // Currency toggle is DATA-DRIVEN: shown iff the EFFECTIVE user's
  // profiles.display_currency is a supported non-USD code (Rayston client → SGD,
  // Nathaniel + Mako → AUD). Onboarding a currency = one-row UPDATE, no code
  // deploy. Uses the EFFECTIVE user (viewAs-aware) so "View As Rayston" shows the
  // [USD|SGD] toggle in Rayston's POV. (Mako ruling 2026-07-16, per-user model.)
  const canToggleCurrency = !!userTargetCurrency(user?.displayCurrency);

  // Permissions (based on effective role)
  const canManageTeam = ["exec", "client", "manager"].includes(role || "");
  const canViewFinancials = ["exec", "client", "closer"].includes(role || "");
  const canViewGlobalStats = role === "exec";
  const canDeleteData = role === "exec";

  // Hard-delete archived client portal tasks (exec only — destructive, no undo).
  // RPC `hard_delete_client_task` also allows super_admin via its own check.
  const canHardDeleteClientTask = role === "exec";

  // Tunnel Vision Check
  const hasTunnelVision = ["closer", "setter", "dialer"].includes(role || "");

  // Feature Flags
  const showClientPortal = ["exec", "client"].includes(role || "");
  const showHiringBoard = ["exec", "client", "manager"].includes(role || "");

  // Leaderboard Permissions
  const canViewAllPods = role === "exec";
  const canSelectSector = ["exec", "manager"].includes(role || "");

  // Role identity flags
  const isCloser = role === "closer";
  const isSetter = role === "setter";
  const isDialer = role === "dialer";
  const isClient = role === "client";
  const isManager = role === "manager";

  // Manager pod scoping — populated at login for manager role, empty for all others
  const managedPodIds: string[] = user?.managedPodIds || [];
  // canSwitchPod: true when manager oversees more than one pod
  const canSwitchPod = managedPodIds.length > 1;

  // Derived layout/feature flags
  const canEditLeaderboard = !hasTunnelVision;
  const showForecastTab = !hasTunnelVision;
  const useBentoLayout = !hasTunnelVision;

  // Triage page flags
  // canAccessTriageList: all internal roles except client
  const canAccessTriageList = ["exec", "manager", "closer", "setter", "dialer"].includes(
    role || ""
  );
  // canSelfSetClaim: only closers and execs can claim a lost lead FOR 12% COMMISSION.
  const canSelfSetClaim = ["closer", "exec"].includes(role || "");
  // canClaimForDialCredit: dialers can ALSO claim a lost lead, but NEVER for money —
  // a successful self-set (revived dead lead books a call) earns dial-KPI credit
  // ("dial less that day"), not the 12%. Split from canSelfSetClaim so the UI never
  // shows a dialer any commission copy and the money path (useInvoice, rep.role==='closer'
  // guarded) can never pay them. Server enforcement = claim_lead/release_claim RPC role
  // gate ('closer','exec','dialer') + role-aware claim_type in custom_data. Mako ruling
  // 2026-07-09; matches locked 2026-06-23 "dialers claim for KPI credit only, never money".
  // + temporary per-person grants (config/tempDialers.ts): a setter who dials for a while
  // keeps their setter role (and pay) but claims here like a dialer. Server mirror in claim_lead.
  const canClaimForDialCredit = canClaimAsDialer(role, user?.id);

  // canSeeWonUpsells: a WON claim (the lead already PAID for high-ticket — there is a
  // sales_call_outcome with status paid_in_full / closed_payment_plan, per the 2026-06-25
  // N5 ruling) is an UPSELL target, not a cold lead. Dialers must never see them — they'd
  // burn dials cold-calling a paying customer. Closers see them branded "P0 · Upsells":
  // people who already bought, call them again to upsell. Setters are excluded too — they
  // can't claim, and Mako's rule is "only closers can". Exec/manager keep them because
  // oversight roles see all org data (CLAUDE.md #1) and silently blanking a won claim from
  // a manager's board would be a regression nobody asked for.
  //
  // Named flag, NOT a raw `role === 'dialer'` check inside the page — .claude/rules/
  // dont-touch.md "closer-visible org-wide pages": a future dev must be able to grep it.
  //
  // ⚠️ VISIBILITY GATE, NOT A SECURITY BOUNDARY. This filters rows in React; the
  // get_lead_claim_history RPC still ships won claims to a dialer's browser, so anyone
  // with DevTools can read them. A HARD gate means filtering server-side by role inside
  // the RPC — a DROP+CREATE on a shared prod function plus a /cachebump (read-shape
  // change). Mako's ruling pending; do NOT describe this as "hidden from dialers"
  // without naming which of the two it is. (Mako ruling 2026-07-13)
  const canSeeWonUpsells = ["closer", "exec", "manager"].includes(role || "");

  // canSeeUpsellSections: the Lost Leads "Upsell" + "Failed Payment Plans" tabs
  // (P-UPSELL-FAILEDPP). HIDDEN FROM EVERYONE except the two super-admins (Mako +
  // Nathaniel, verified 2 of 2 live 2026-09-24) until Mako opens them up — Mako
  // 2026-09-24: "it's hidden to everyone at the moment EXCEPT nathaniel and me".
  // Reads realUser, NOT the view-as user: "View as closer" must not hide the tabs
  // from Mako, and must never grant them to anyone else. The server-side wall is
  // get_triage_upsell_list's is_super_admin check (42501 for every other caller).
  const canSeeUpsellSections = !!realUser?.isSuperAdmin;

  // canSeeLostLeadsAnalytics: the Lost Leads "Analytics" tab (per-tier funnels,
  // per-rep release audit, revived-deals ledger). Exec + manager (Mako ruling
  // 2026-10-01, /ham default 7). Named flag per dont-touch.md org-wide-page rule.
  // Server wall = get_lost_leads_analytics role gate (42501 for everyone else).
  const canSeeLostLeadsAnalytics =
    ["exec", "manager", "owner", "admin"].includes(role || "") || !!realUser?.isSuperAdmin;

  // canSeeWebinarCoverage: the Lost Leads "Webinar" tab (webinar calling numbers WITH
  // lead names + phones, a work list for phone setters). Mako WD5 = A 2026-10-07:
  // "A: Lost Leads, new Webinar tab". Phone setters (dialer) + exec/manager. Closers
  // and setters are left out until Mako says otherwise. Named flag per dont-touch.md.
  // Server wall = get_lost_leads_webinar_calls role gate (42501 for everyone else).
  const canSeeWebinarCoverage =
    ["exec", "manager", "owner", "admin", "dialer"].includes(role || "") ||
    !!realUser?.isSuperAdmin;

  // canSeePartials: the Lost Leads "Partials" tab (people who started an application and
  // stopped: name, email, phone, resume link). Nathaniel 2026-10-08 Zoom clip: "add a new tab
  // to the lost leads called partial submissions"; phone setters dial them, DM setters reach
  // them. So dialer + setter + exec/manager. Closers left out. Named flag per dont-touch.md.
  // Server wall = get_lost_leads_partials role gate (42501 for everyone else).
  const canSeePartials =
    ["exec", "manager", "owner", "admin", "dialer", "setter"].includes(role || "") ||
    !!realUser?.isSuperAdmin;

  // Transaction Log page — public-by-design within an org (per Mako 2026-05-13).
  // All in-org roles see all reps' transactions. Companion RLS policy
  // `transactions_tunnel_vision_select` enforces org isolation at the DB layer.
  // Pattern mirrors `canAccessTriageList` (May 2026 Triage precedent).
  const canSeeAllRepTransactions = [
    "exec",
    "manager",
    "client",
    "owner",
    "admin",
    "viewer",
    "closer",
    "setter",
    "dialer",
  ].includes(role || "");

  // DM Analytics dismiss permissions (S29, May 2026)
  // canDismissOwnConversations: setters can dismiss/kill conversations assigned to them.
  // canDismissAnyConversation: managers/execs can dismiss any conversation org-wide.
  // Closers/dialers/clients/viewers cannot dismiss IG conversations — server-side RPC also rejects them.
  const canDismissOwnConversations = ["setter", "manager", "exec", "owner", "admin"].includes(
    role || ""
  );
  const canDismissAnyConversation = ["manager", "exec", "owner", "admin"].includes(role || "");

  // Dial Analytics — org-wide dial data; closers + dialers see ALL dialers
  // (transparency mirror of DM Analytics, Mako 2026-05-03; closers added Jul 2026,
  // manager removed). Sidebar link shows only for closer/dialer (flat layout);
  // exec keeps omniscient URL access but has no sidebar link (dropped from the
  // sectioned Management layout). Named flag per dont-touch.md precedent
  // (canAccessTriageList). RouteGuard + roleConfig are the primary gate; this is
  // the in-page defense-in-depth check.
  // + a setter holding a temp dialer grant (config/tempDialers.ts, Norman until 2026-11-27).
  const canAccessDialAnalytics = canSeeDialAnalytics(role, user?.id);

  // canAccessWorkforceList: the /admin/workforce people console is admin-facing
  // only (exec + manager). Super-admins get exec role, so they pass. Mirrors the
  // RLS on rep_onboarding/coaching_notes (role IN ('exec','manager') OR super-admin).
  const canAccessWorkforceList = ["exec", "manager"].includes(role || "");

  // canViewRepPrivateDetails: who sees a rep's sensitive PII (bank/payout, address,
  // phone, birthday, emergency contact) in the Workforce dossier + on the invoice PDF.
  // Exec/owner/admin (or self, or super-admin) only — managers EXCLUDED (Mako 2026-09-11,
  // "managers shouldn't see it for now"). Mirrors the rep_private_details RLS. Named flag
  // per dont-touch.md closer-visible-page doctrine. DB RLS is the real gate; this hides UI.
  const canViewRepPrivateDetails = ["owner", "admin", "exec"].includes(role || "");

  // Dial call recordings — visible to EVERY in-org role (Mako ruling 2026-07-15,
  // "nah lets everyone listen to it dawg"). Before this, the gate was
  // `canManageTeam || isDialer`, which silently blocked all 9 closers + 3 setters
  // in the UI ("Recording available to managers only") — Adrian, a closer, hit it.
  // Same public-within-org doctrine as canSeeAllRepTransactions / canSeeAllInstallments.
  //
  // ⚠️ TWO-LAYER GATE — this flag only decides whether the player BUTTON renders.
  // Pressing play calls supabase.storage.from('dial-recordings').createSignedUrl(),
  // which is gated server-side by the `dial_recordings_select` RLS policy. Both
  // layers MUST list the same roles or the button appears then errors. The policy
  // was widened to "any member of the object's org" in
  // sql/migrations/2026-07-15_dial_recordings_select_all_org_members.sql — org
  // isolation (folder segment[1] = org_id) is still enforced there. Named flag per
  // dont-touch.md "closer-visible org-wide pages" so a future dev can grep it.
  const canViewDialRecordings = [
    "exec",
    "manager",
    "client",
    "owner",
    "admin",
    "viewer",
    "closer",
    "setter",
    "dialer",
  ].includes(role || "");

  // Installments page — org-wide within an org (Jul 2026, Mako). Same doctrine as
  // canSeeAllRepTransactions / Dial Analytics / Triage. Collections is a shared
  // function; installment $ is already rep-visible via Transaction Log.
  const canSeeAllInstallments = [
    "exec",
    "manager",
    "client",
    "owner",
    "admin",
    "viewer",
    "closer",
    "setter",
    "dialer",
  ].includes(role || "");

  // Claim an orphaned installment (original rep inactive). Server-side
  // claim_installment RPC is the real gate (SECURITY DEFINER); this flag gates
  // the in-app button. Mirrors canSelfSetClaim precedent.
  const canClaimInstallment = ["exec", "manager", "closer", "setter", "dialer"].includes(
    role || ""
  );

  return {
    role,
    isRealExec,
    canManageTeam,
    canViewFinancials,
    canViewGlobalStats,
    canDeleteData,
    canHardDeleteClientTask,
    hasTunnelVision,
    showClientPortal,
    showHiringBoard,
    canViewAllPods,
    canSelectSector,
    isCloser,
    isSetter,
    isDialer,
    isClient,
    isManager,
    managedPodIds,
    canSwitchPod,
    canEditLeaderboard,
    showForecastTab,
    useBentoLayout,
    canAccessTriageList,
    canSelfSetClaim,
    canClaimForDialCredit,
    canSeeWonUpsells,
    canSeeUpsellSections,
    canSeeLostLeadsAnalytics,
    canSeeWebinarCoverage,
    canSeePartials,
    canSeeAllRepTransactions,
    canDismissOwnConversations,
    canDismissAnyConversation,
    canAccessDialAnalytics,
    canAccessWorkforceList,
    canViewRepPrivateDetails,
    canViewDialRecordings,
    canSeeAllInstallments,
    canClaimInstallment,
    canToggleCurrency,
  };
};
